import React from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
} from '@mui/material';

const policyData = {
  terms: {
    title: 'Terms & Conditions',
    lastUpdated: 'July 22, 2026',
    sections: [
      {
        heading: '1. Acceptance of Terms',
        content: 'By accessing and using iSoret (referred to as "the Platform"), developed and operated by FLUX INFOTECH LLP, you agree to comply with and be bound by these Terms & Conditions. If you do not agree, please refrain from using the Platform.',
      },
      {
        heading: '2. Eligibility and Account Registration',
        content: 'You must be at least 18 years of age to register an account and claim digital coupons on iSoret. You are responsible for maintaining the confidentiality of your credentials and all activities occurring under your account.',
      },
      {
        heading: '3. Coupon Claims and Limitations',
        content: 'Coupons on our platform represent promotional offers with assured savings or cashback. Vouchers are subject to stock availability and vendor terms. iSoret reserves the right to limit the number of claims per user for any coupon.',
      },
      {
        heading: '4. Intellectual Property',
        content: 'All materials, including logos, graphics, code, and content, are the intellectual property of FLUX INFOTECH LLP or our partner brands. Unauthorized distribution or copying is strictly prohibited.',
      },
      {
        heading: '5. Limitation of Liability',
        content: 'iSoret acts as a promotional coupon marketplace. While we verify all listed offers daily, we are not responsible for the products, services, or warranty claims of partner brands once you redeem a coupon on their platform.',
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    lastUpdated: 'July 22, 2026',
    sections: [
      {
        heading: '1. Information We Collect',
        content: 'We collect personal information necessary to deliver coupon services, including your Full Name, Email Address, Contact Number, and wallet balance details. No bank details or payment credentials are stored on our systems.',
      },
      {
        heading: '2. How We Use Your Data',
        content: 'Your information is used solely to authenticate your account, process coupon claims, credit cashback rewards to your wallet, and send relevant deal notifications. We do not sell your personal data to third parties.',
      },
      {
        heading: '3. Data Security',
        content: 'We implement industry-standard encryption and security measures to protect your personal data from unauthorized access, alteration, or disclosure. All active sessions are protected by secure tokens.',
      },
      {
        heading: '4. Cookies and Tracking',
        content: 'iSoret uses cookies to maintain session states and improve platform navigation. We do not run intrusive cross-site tracking scripts or sell user behavior data.',
      },
    ],
  },
  refund: {
    title: 'Refund Policy',
    lastUpdated: 'July 22, 2026',
    sections: [
      {
        heading: '1. Nature of Digital Coupons',
        content: 'All coupon claims and voucher redemptions processed through iSoret are digital in nature. Once a coupon has been successfully claimed and the code is displayed or assigned to your wallet, it cannot be returned or refunded.',
      },
      {
        heading: '2. Cashback and Wallet Credits',
        content: 'Cashback values are credited to your wallet in accordance with specific offer milestones. If a partner brand fails to honor a verified coupon or if there is a tracking issue, please contact support. Valid disputes will be resolved through wallet balance adjustments.',
      },
      {
        heading: '3. Transaction Fees',
        content: 'If there are any platform fees or withdrawal charges applied during your wallet withdrawal request, these are non-refundable once the bank transfer request has been initiated.',
      },
    ],
  },
  cancellation: {
    title: 'Cancellation Policy',
    lastUpdated: 'July 22, 2026',
    sections: [
      {
        heading: '1. Account Deactivation',
        content: 'Users can request to deactivate or cancel their iSoret account at any time through the Profile section or by contacting our support team at isoret.server@gmail.com.',
      },
      {
        heading: '2. Cancelling Coupon Claims',
        content: 'Since coupon codes are allocated instantly from limited stocks, claimed coupons cannot be canceled or returned to the active pool. Claimed coupons that remain unused will simply expire on their documented expiration date without affecting your wallet balance.',
      },
      {
        heading: '3. Platform Rights',
        content: 'iSoret reserves the right to cancel or void coupon claims, terminate accounts, or revoke cashback rewards in cases of detected fraudulent activity, multi-account abuse, or violation of our Terms & Conditions.',
      },
    ],
  },
};

export default function Policy({ type }) {
  const data = policyData[type] || policyData.terms;

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 70px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        padding: { xs: '40px 16px', md: '80px 24px' },
        position: 'relative',
        background: '#0F2E2B',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(650px 450px at 50% -10%, rgba(212,162,76,0.1), transparent 60%)
          `,
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            background: 'rgba(21, 58, 53, 0.6)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(245, 241, 230, 0.12)',
            borderRadius: '24px',
            padding: { xs: 4, md: 6 },
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
            color: '#F5F1E6',
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Fraunces", serif',
              fontWeight: 700,
              color: '#F5F1E6',
              mb: 1,
              textAlign: 'center',
            }}
          >
            {data.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#D4A24C',
              fontWeight: 500,
              textAlign: 'center',
              mb: 5,
              fontFamily: '"JetBrains Mono", monospace',
            }}
          >
            Last Updated: {data.lastUpdated}
          </Typography>

          {data.sections.map((section, index) => (
            <Box key={index} sx={{ mb: 4, '&:last-child': { mb: 0 } }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontWeight: 600,
                  color: '#D4A24C',
                  mb: 1.5,
                  textAlign: 'left',
                }}
              >
                {section.heading}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(245, 241, 230, 0.8)',
                  lineHeight: 1.7,
                  textAlign: 'left',
                }}
              >
                {section.content}
              </Typography>
            </Box>
          ))}
        </Paper>
      </Container>
    </Box>
  );
}
